import {useRespondToPermissionUpgradeRequestMutation, useUpdateCompanyMemberPermissionMutation} from "@/api/companyApi";
import ChangeAccountOwnershipModal from "@/components/MultiAccount/ChangeAccountOwnershipModal";
import PermissionSelect from "@/components/MultiAccount/PermissionSelect";
import RemoveCompanyMemberModal from "@/components/MultiAccount/RemoveCompanyMemberModal";
import {useIsAdmin, useSelectedCompany, useUserId, useUserPermission} from "@/hooks/useUserHooks";
import {AbilityContext} from "@/permissions";
import useCompanyStore from "@/store/company";
import {PERMISSIONS} from "@/store/constants";
import type {TAccountMember, TPermission} from "@/store/types";
import {useContext, useMemo, useState, type FC} from "react";
import {useTranslation} from "react-i18next";

const MAKE_OWNER = "MAKE_OWNER";
const REMOVE = "REMOVE";
const LEAVE = "LEAVE";

const UpdateUserPermission: FC<{
  companyMember: TAccountMember;
  permission: TPermission;
  isCompanyEdit?: boolean;
}> = ({companyMember, permission, isCompanyEdit = false}) => {
  const {t} = useTranslation("multi_account");
  const abilityContext = useContext(AbilityContext);
  const loggedInUserId = useUserId();
  const isAdmin = useIsAdmin();
  const companyFromStore = useCompanyStore(state => state.company);
  const selectedCompany = useSelectedCompany();
  const company = useMemo(
    () => (isAdmin ? companyFromStore : selectedCompany),
    [isAdmin, companyFromStore, selectedCompany],
  );
  const isViewingSelf = useMemo(
    () => companyMember.user.id === loggedInUserId,
    [companyMember.user.id, loggedInUserId],
  );
  const userPermission = useUserPermission({excludeAdmins: isViewingSelf});
  const isCompanyOwner = useMemo(() => userPermission === "ADMIN", [userPermission]);
  const canUpdateMembers = useMemo(() => abilityContext.can("update", "CompanyMembers"), [abilityContext]);
  const canDeleteMembers = useMemo(() => abilityContext.can("delete", "CompanyMember"), [abilityContext]);
  const [memberToRemove, setMemberToRemove] = useState<TAccountMember | null>(null);
  const [memberToTransferOwnershipTo, setMemberToTransferOwnershipTo] = useState<TAccountMember | null>(null);

  const {mutateAsync: updatePermission} = useUpdateCompanyMemberPermissionMutation({
    companyMemberUserId: companyMember.user.id!,
    companyId: company?.id!,
  });
  const {mutate: upgradePermission} = useRespondToPermissionUpgradeRequestMutation({companyId: company?.id!});

  return (
    <>
      {!!memberToTransferOwnershipTo && (
        <ChangeAccountOwnershipModal
          companyId={company?.id!}
          companyMember={companyMember}
          onClose={() => setMemberToTransferOwnershipTo(null)}
        />
      )}
      {!!memberToRemove && (
        <RemoveCompanyMemberModal
          companyId={company?.id!}
          companyName={company?.company_name!}
          companyMember={companyMember}
          onClose={() => setMemberToRemove(null)}
        />
      )}
      <PermissionSelect
        value={permission}
        onChange={async value => {
          if (value === MAKE_OWNER) {
            setMemberToTransferOwnershipTo(companyMember);
            return;
          }

          if (value === REMOVE || value === LEAVE) {
            setMemberToRemove(companyMember);
            return;
          }

          if (!canUpdateMembers || value === permission) {
            return;
          }

          await updatePermission({
            newPermission: value as TPermission,
          });

          const permissionRequestStatus = companyMember.permission_request?.status;
          const requestedPermission = companyMember.permission_request?.permission_type;

          if (!!requestedPermission && permissionRequestStatus === "PENDING" && requestedPermission === value) {
            upgradePermission({
              user_id: companyMember.user.id!,
              status: "ACCEPTED",
            });
          }
        }}
        topOptions={
          isCompanyEdit && isCompanyOwner
            ? [
                {
                  value: MAKE_OWNER,
                  label: t(MAKE_OWNER.toLowerCase()),
                },
              ]
            : []
        }
        bottomOptions={
          isCompanyEdit
            ? isViewingSelf
              ? [{value: LEAVE, label: t(LEAVE.toLowerCase())}]
              : canDeleteMembers
                ? [{value: REMOVE, label: t(REMOVE.toLowerCase())}]
                : []
            : []
        }
        excludedPermissions={
          !canUpdateMembers || (!isCompanyOwner && isViewingSelf)
            ? PERMISSIONS.filter(permission => permission !== userPermission)
            : []
        }
        containerClassName="max-w-[185px]"
      />
    </>
  );
};

export default UpdateUserPermission;
